import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { User } from '../types';
import { USER_TYPES } from '../constants/UserConstants';
import axios from 'axios';
import { preventCachedImage } from '../utils/helperFunctions';

interface AuthContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  role: keyof typeof USER_TYPES | undefined;
  loggingOut: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setStateUser] = useState<User | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);

  const isAuthenticated = useMemo(
    () => !!localStorage.getItem('token') || user !== null,
    [user]
  );

  const role = useMemo(
    () =>
      (localStorage.getItem('userType') as keyof typeof USER_TYPES) ||
      (user?.type as keyof typeof USER_TYPES | undefined),
    [user]
  );

  const setUser = useCallback((user: User | null) => {
    if (user) {
      setStateUser({
        ...user,
        image: preventCachedImage(user.image),
      });
    } else {
      setStateUser(null);
    }
  }, []);

  const logout = useCallback(async () => {
    setLoggingOut(true);
    try {
      const token = localStorage.getItem('token');
      const fcmToken = localStorage.getItem('fcmToken');

      if (token && fcmToken) {
        await axios.put(
          `${process.env.REACT_APP_URL}/notifications/unsubscribe`,
          { token: fcmToken },
         { headers: { Authorization: `Bearer ${token}`, 'X-Client-Type': 'web' } }
        );
      }
    } catch  {
    }

    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    setLoggingOut(false);
  }, [setUser]);

  useEffect(() => {
    if (!user) {
      const token = localStorage.getItem('token');

      if (token) {
        axios
          .get(`${process.env.REACT_APP_URL}/me/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          .then((response) => {
            setUser(response.data);
          })
          .catch(() => {
            logout();
          });
      }
    }
  }, [user, logout, setUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        role,
        loggingOut,
        setUser,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(
      'useNotificationsContext must be used within NotificationsProvider'
    );
  }
  return context;
};
